import { CSSProperties, ReactNode } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import {
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
  IProductionDashboardSession,
  ProductionDashboardAssemblyKind,
} from "../../models/ProductionDashboard";
import { assemblyElapsedSeconds, formatAssemblyDuration } from "../../utils/assemblyElapsed";
import { BayCardState, bayState, vehicleState } from "../../utils/bayCardState";
import classNames from "../../utils/classNames";
import { IFlowLane, IFlowLaneColumn } from "../../utils/flowLaneColumns";
import { formatDurationHms, sessionElapsedSeconds } from "../../utils/sessionElapsed";
import { BAY_STATE_DARK, TV_FLOW_BLOCK } from "./bayStateStyles";

interface TvLineLaneProps {
  bay: IProductionDashboardBay;
  lane: IFlowLane;
  now: DateTime;
}

const vehicleName = (v: { combinedMake: string | null; combinedModel: string | null }) =>
  [v.combinedMake, v.combinedModel].filter(Boolean).join(" ");

// A vehicle at a station: a solid block in the state colour (green working, amber past the
// warning time, red over target, blue finished and waiting to move on, grey stopped) carrying the
// plate, the model and the live timer against the station's target. Nothing on it is clickable -
// this is for a wall, not a desk.
function TvFlowVehicle({ vehicle, now }: { vehicle: IProductionDashboardBayVehicle; now: DateTime }) {
  const { t } = useTranslation("production");
  const state = vehicleState(vehicle, now);

  return (
    <div className={classNames("rounded-lg px-3 py-2.5 flex flex-col gap-1.5", TV_FLOW_BLOCK[state])}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="rounded bg-yellow-300 px-2 text-2xl font-bold tracking-wider text-gray-900 whitespace-nowrap">
          {vehicle.vrm || t("No VRM")}
        </span>
        <span className="text-base truncate opacity-90">
          {vehicleName(vehicle) || vehicle.stockNumber || "-"}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-semibold leading-none tabular-nums">
          {formatAssemblyDuration(assemblyElapsedSeconds(vehicle, now))}
        </span>
        {vehicle.currentAssemblyAverageMinutes ? (
          <span className="text-base opacity-80">/ {vehicle.currentAssemblyAverageMinutes}m</span>
        ) : null}
      </div>
    </div>
  );
}

// A triage/depollution session on a vehicle that isn't on the lane yet. Stages carry no target or
// warning time, so there is no amber or red here: green while the timer runs, grey when paused.
function TvFlowSession({ session, now }: { session: IProductionDashboardSession; now: DateTime }) {
  const { t } = useTranslation("production");
  const state: BayCardState = session.startedOn ? "working" : "paused";

  return (
    <div className={classNames("rounded-lg px-3 py-2.5 flex flex-col gap-1.5", TV_FLOW_BLOCK[state])}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="rounded bg-yellow-300 px-2 text-2xl font-bold tracking-wider text-gray-900 whitespace-nowrap">
          {session.vrm || t("No VRM")}
        </span>
        <span className="text-base truncate opacity-90">
          {vehicleName(session) || session.stockNumber || "-"}
        </span>
      </div>
      <span className="text-4xl font-semibold leading-none tabular-nums">
        {formatDurationHms(sessionElapsedSeconds(session, now))}
      </span>
    </div>
  );
}

function TvFlowColumn({
  label,
  name,
  state,
  children,
}: {
  label: string;
  name: string;
  state: BayCardState;
  children: ReactNode;
}) {
  return (
    <div
      className={classNames(
        "rounded-xl border-2 p-3 flex flex-col gap-2 min-w-0",
        BAY_STATE_DARK[state].card
      )}
    >
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</div>
        <div className="text-lg font-semibold text-gray-100 truncate">{name}</div>
      </div>
      {children}
    </div>
  );
}

export default function TvLineLane({ bay, lane, now }: TvLineLaneProps) {
  const { t } = useTranslation("production");

  // Both rows share the main row's track widths, so a sub-assembly column sits under a main
  // column of the same size rather than stretching across the screen. Inline because the count
  // is data-driven and Tailwind can't see a dynamic grid-cols class.
  const gridStyle: CSSProperties = {
    gridTemplateColumns: `repeat(${Math.max(1, lane.main.length)}, minmax(0, 1fr))`,
  };

  const columnName = (column: IFlowLaneColumn): string => {
    switch (column.assembly.kind) {
      case ProductionDashboardAssemblyKind.Triage:
        return t("Triage");
      case ProductionDashboardAssemblyKind.Depollution:
        return t("Depollution");
      default:
        return column.assembly.name === "Non-Assembly" ? t("Other") : column.assembly.name;
    }
  };

  const renderColumn = (column: IFlowLaneColumn) => {
    const { assembly, assemblyNumber, vehicles, sessions } = column;
    const isEmpty = vehicles.length === 0 && sessions.length === 0;
    // Sessions have no target so they never raise the column past "working".
    const state: BayCardState = isEmpty
      ? "idle"
      : vehicles.length > 0
        ? bayState(vehicles, now)
        : "working";

    return (
      <TvFlowColumn
        key={assemblyNumber != null ? assembly.name : `stage-${assembly.kind}`}
        label={
          assemblyNumber != null ? t("Assembly {{number}}", { number: assemblyNumber }) : t("Stage")
        }
        name={columnName(column)}
        state={state}
      >
        {isEmpty ? (
          <div className="text-lg text-gray-600 text-center py-3">—</div>
        ) : (
          <div className="flex flex-col gap-2">
            {vehicles.map((v) => (
              <TvFlowVehicle key={v.id} vehicle={v} now={now} />
            ))}
            {sessions.map((s) => (
              <TvFlowSession key={s.id} session={s} now={now} />
            ))}
          </div>
        )}
      </TvFlowColumn>
    );
  };

  return (
    <div className="shrink-0 rounded-2xl border border-gray-800 bg-gray-900/60 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-3xl font-bold leading-none truncate">{bay.name}</span>
        <span className="text-base text-gray-400 shrink-0">
          {lane.inProgressCount > 0
            ? t("{{count}} in progress", { count: lane.inProgressCount })
            : t("No vehicle")}
        </span>
      </div>

      {/* Main assemblies flow left to right */}
      <div className="grid gap-3" style={gridStyle}>
        {lane.main.map(renderColumn)}
      </div>

      {/* Sub assemblies sit underneath, keeping their flow-order numbering and the main row's widths */}
      {lane.sub.length > 0 && (
        <div className="grid gap-3" style={gridStyle}>
          {lane.sub.map(renderColumn)}
        </div>
      )}
    </div>
  );
}
